<template>
  <div class="w-scroll">
    <div v-if="!rows.length" class="w-empty"><i class="fas fa-flag"></i>{{ stats.byCountry ? 'No countries yet' : 'Loading…' }}</div>
    <div v-for="(c, i) in rows" :key="c.country" class="ct w-row-in" :style="{ animationDelay: (i * 0.07) + 's' }">
      <span class="ct-flag">{{ flag(c.country) }}</span>
      <div class="ct-main">
        <div class="ct-line">
          <span class="ct-name">{{ c.country }}</span>
          <span class="ct-co2"><b>{{ compact(c.co2) }}</b> t CO2</span>
        </div>
        <div class="w-bar"><span :style="{ '--w': c.share + '%', background: gradient(i), animationDelay: (0.2 + i * 0.07) + 's' }"></span></div>
        <div class="ct-meta">{{ fmt(c.forests) }} forests · {{ fmt(c.ha, 1) }} ha · {{ compact(c.trees) }} trees</div>
      </div>
    </div>
  </div>
</template>

<script>
import { compact, fmt, seriesColors } from '../theme';

// flags of the countries the platform works with (others get a globe)
const ISO = {
  Finland: 'FI', Spain: 'ES', Sweden: 'SE', Norway: 'NO', Estonia: 'EE', Latvia: 'LV', Lithuania: 'LT', Portugal: 'PT', France: 'FR', Germany: 'DE',
  Italy: 'IT', Poland: 'PL', Ireland: 'IE', 'United Kingdom': 'GB', Netherlands: 'NL', Belgium: 'BE', Austria: 'AT', Switzerland: 'CH', Denmark: 'DK',
  'United Arab Emirates': 'AE', UAE: 'AE', India: 'IN', Brazil: 'BR', Mexico: 'MX', Colombia: 'CO', Peru: 'PE', Chile: 'CL', Argentina: 'AR', Kenya: 'KE',
  Uganda: 'UG', Tanzania: 'TZ', Ghana: 'GH', Nigeria: 'NG', 'South Africa': 'ZA', Morocco: 'MA', Indonesia: 'ID', Philippines: 'PH', Vietnam: 'VN',
  'United States': 'US', USA: 'US', Canada: 'CA', Australia: 'AU', 'New Zealand': 'NZ', Japan: 'JP', China: 'CN', Romania: 'RO', Greece: 'GR', Croatia: 'HR',
};

export default {
  name: 'Countries',
  props: { stats: { type: Object, required: true } },
  computed: {
    rows() {
      const rows = (this.stats.byCountry || []).map((c) => ({ country: c.country, forests: Number(c.forests) || 0, ha: Number(c.totalHa) || 0, trees: Number(c.totalTrees) || 0, co2: Number(c.totalCo2) || 0 }));
      const max = Math.max(1, ...rows.map((r) => r.co2));
      return rows.map((r) => ({ ...r, share: Math.max(3, (r.co2 / max) * 100) }));
    }
  },
  methods: {
    fmt,
    compact,
    flag(name) {
      const iso = ISO[name];
      return iso ? String.fromCodePoint(...[...iso].map((ch) => 127397 + ch.charCodeAt(0))) : '🌍';
    },
    gradient(i) {
      const c = seriesColors();
      return `linear-gradient(90deg, ${c[i % c.length]}, ${c[(i + 1) % c.length]})`;
    }
  }
};
</script>

<style scoped lang="scss">
@import '../widgets.scss';

.ct {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 9px 4px;
  border-bottom: 1px dashed var(--border-color);

  &:last-child { border-bottom: none; }
}

.ct-flag {
  font-size: 1.7rem;
  line-height: 1;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  flex: 0 0 40px;
}

.ct-main { flex: 1 1 auto; min-width: 0; }

.ct-line {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 5px;
}

.ct-name { font-weight: 700; color: var(--text-heading); }
.ct-co2 { font-size: 0.8rem; color: var(--text-muted); b { color: var(--text-heading); } }
.ct-meta { margin-top: 4px; font-size: 0.72rem; color: var(--text-muted); }
</style>
